import type { JSX } from 'react';

import { MemoryDiff } from '@/features/memory/MemoryDiff.js';
import { changedLines } from '@/features/memory/line-diff.js';
import type { SkillResource, SkillRevisionDetail } from '@/lib/api/skills.js';

/** The text parts of a version, in the order they are shown. */
const TEXT_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'instructions', label: 'Instructions' },
] as const;

/** Paths added, removed, and kept under the same path with new content, between two versions. */
interface FileChanges {
  added: string[];
  removed: string[];
  replaced: string[];
}

/** Compares two file lists by path; the same path with a different stored object counts as replaced. */
function compareFiles(
  before: SkillResource[] | null,
  after: SkillResource[] | null,
): FileChanges {
  const beforeKeys = new Map(
    (before ?? []).map((resource) => [resource.path, resource.s3_key]),
  );
  const afterKeys = new Map(
    (after ?? []).map((resource) => [resource.path, resource.s3_key]),
  );
  const added: string[] = [];
  const replaced: string[] = [];
  for (const [path, s3Key] of afterKeys) {
    const previousKey = beforeKeys.get(path);
    if (previousKey === undefined) added.push(path);
    else if (previousKey !== s3Key) replaced.push(path);
  }
  const removed = [...beforeKeys.keys()].filter((path) => !afterKeys.has(path));
  return { added, removed, replaced };
}

/** One line per non-empty file change, e.g. "Added: scripts/x.py". */
function fileChangeLines(changes: FileChanges): string[] {
  const lines: string[] = [];
  if (changes.added.length > 0)
    lines.push(`Added: ${changes.added.join(', ')}`);
  if (changes.removed.length > 0)
    lines.push(`Removed: ${changes.removed.join(', ')}`);
  if (changes.replaced.length > 0)
    lines.push(`Replaced: ${changes.replaced.join(', ')}`);
  return lines;
}

/**
 * What one version changed against the version before it: the changed lines of the title,
 * description and instructions, each row marked `−` or `+` (never colour alone), then the files
 * added, removed or replaced. `before` is null for version 1, which has nothing to compare with.
 */
export function SkillRevisionChanges({
  before,
  after,
}: {
  before: SkillRevisionDetail | null;
  after: SkillRevisionDetail;
}): JSX.Element {
  return (
    <section
      aria-label="Changes in this version"
      className="flex flex-col gap-2"
    >
      <h4 className="text-xs font-semibold text-foreground">
        Changes in this version
      </h4>
      {before === null ? (
        <p className="text-xs text-muted-foreground">First version.</p>
      ) : (
        <ChangeList before={before} after={after} />
      )}
    </section>
  );
}

function ChangeList({
  before,
  after,
}: {
  before: SkillRevisionDetail;
  after: SkillRevisionDetail;
}): JSX.Element {
  const textChanges = TEXT_FIELDS.map((field) => ({
    label: field.label,
    lines: changedLines(before[field.key] ?? '', after[field.key] ?? ''),
  })).filter((change) => change.lines.length > 0);
  const fileLines = fileChangeLines(
    compareFiles(before.resources, after.resources),
  );
  if (textChanges.length === 0 && fileLines.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No changes to the text or files.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {textChanges.map((change) => (
        <div
          key={change.label}
          role="group"
          aria-label={change.label}
          className="flex flex-col gap-1"
        >
          <span className="text-xs text-muted-foreground">{change.label}</span>
          <MemoryDiff lines={change.lines} />
        </div>
      ))}
      {fileLines.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          {fileLines.map((line) => (
            <li key={line} className="break-words">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
